import type { RefObject } from 'react'

import { useEffect } from 'react'

export type ScrollMode = 'through' | 'pin' | 'enter'

// Writes a --p value from 0 to 1 onto the element as the page scrolls, so CSS
// can tie gentle motion to scroll position. Before scripts run, and for anyone
// who asks for reduced motion, --p is left at its final state (1).
//   through: from the element entering at the bottom to leaving at the top
//   pin:     across a tall track whose child is position: sticky
//   enter:   from entering at the bottom until its top reaches a quarter of the screen
export const useScrollProgress = (ref: RefObject<HTMLElement | null>, mode: ScrollMode = 'through'): void => {
    useEffect(() => {
        const element = ref.current

        if (!element) return

        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)')
        let frame = 0

        const update = () => {
            frame = 0

            if (reduce.matches) {
                element.style.setProperty('--p', '1')
                return
            }

            const rect = element.getBoundingClientRect()
            const viewport = window.innerHeight || 1
            let progress = 0

            if (mode === 'pin') progress = -rect.top / Math.max(1, rect.height - viewport)
            else if (mode === 'enter') progress = (viewport - rect.top) / (viewport * 0.75)
            else progress = (viewport - rect.top) / (rect.height + viewport)

            element.style.setProperty('--p', Math.min(1, Math.max(0, progress)).toFixed(4))
        }

        const schedule = () => {
            if (!frame) frame = window.requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', schedule, { passive: true })
        window.addEventListener('resize', schedule)
        reduce.addEventListener('change', schedule)

        return () => {
            window.cancelAnimationFrame(frame)
            window.removeEventListener('scroll', schedule)
            window.removeEventListener('resize', schedule)
            reduce.removeEventListener('change', schedule)
            element.style.removeProperty('--p')
        }
    }, [ref, mode])
}
